/// <reference types="google.maps" />
import {
  OnInit,
  Component,
  ViewChild,
  ElementRef,
  Input,
  ChangeDetectorRef,
} from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Subject, Subscription } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  filter,
  tap,
} from 'rxjs/operators';

import { APIErrorResponse, Dictionary, ResponseErrorHandler } from 'asap-team/asap-tools';

import type {
  Profile,
  Address,
  UserRole,
  FormattedAddress,
  Lead,
  NormalizedPlaceData,
} from '@core/types';

// Constants
import { ERROR, USER_ROLE } from '@consts/consts';
import { ROUTE } from '@consts/routes';

// Services
import { LeadsService } from '@core/services/leads/leads.service';
import { GooglePlacesService } from '@core/services/google-places/google-places.service';
import { AsaplyticsService } from '@core/helpers/asaplytics/asaplytics.service';
import { GoogleAutocompleteService } from '@core/services/google-autocomplete/google-autocomplete.service';
import { TypedFormGroup } from '@common/types/form-group-config.type';

// Components
import { AutocompleteSuggestionsComponent } from '@common/components/autocomplete-suggestions/autocomplete-suggestions.component';

interface AddressQuery {
  address: string;
  unit: string;
}

@UntilDestroy()
@Component({
  selector: 'property-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.sass'],
})
export class SearchComponent implements OnInit {

  @ViewChild('search', { static: true }) searchElementRef: ElementRef;

  @ViewChild(AutocompleteSuggestionsComponent) suggestionsComponent: AutocompleteSuggestionsComponent;

  @Input() user: Profile;

  @Input() addressFromQuery: Dictionary<string>;

  USER_ROLE: UserRole = USER_ROLE;

  form: FormGroup;

  errors: string[] = [];

  address: Address;

  action$: Subscription;

  suggestions: google.maps.places.AutocompleteSuggestion[] = [];

  showSuggestions: boolean = false;

  isLoadingSuggestions: boolean = false;

  private searchInput$: Subject<string> = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private leadsService: LeadsService,
    private googlePlacesService: GooglePlacesService,
    private responseErrorHandlerService: ResponseErrorHandler,
    private asaplyticsService: AsaplyticsService,
    private googleAutocompleteService: GoogleAutocompleteService,
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group<TypedFormGroup<AddressQuery>>({
      address: [this.addressFromQuery?.address || '', Validators.required],
      unit: [{ value: this.addressFromQuery?.unit || '', disabled: true }],
    });
    this.subscribes();

    if (this.addressFromQuery?.encodedPlace) {
      const decodedAddress: object = JSON.parse(atob(this.addressFromQuery.encodedPlace));

      this.handleAddressChange(decodedAddress);

      if (
        this.address?.country
        && this.address?.zip
        && this.address?.state
        && this.address?.city
        && this.address?.street
      ) {
        this.submit();
      }

    }
  }

  getMarkerClass(): boolean {
    const control: AbstractControl = this.form.controls.address;

    return (!control.valid && control.touched) || !!this.errors.length;
  }

  submit(): void {
    this.asaplyticsService.trackClickGetStarted();
    this.resetError();

    if (!this.form.value?.address) {
      this.errors.push(ERROR.DEFAULT_SEARCH_ERROR);

      return;
    }

    const { unit } = this.form.value;

    if (unit) {
      this.address = { ...this.address, unit };
    }

    this.action$ = this
      .leadsService
      .validate(this.user.username, this.address)
      .pipe(
        switchMap(() => this.leadsService.search(this.user.username, this.address)),
        untilDestroyed(this),
      )
      .subscribe({
        next: (lead: Lead) => {
          this.router.navigate([`${ROUTE.name.STEPPER}/${lead.uid}`], { relativeTo: this.route });
        },
        error: (error: APIErrorResponse) => {
          this.asaplyticsService.trackClickGetStartedError();
          this.errors = this.responseErrorHandlerService.processWithoutControls(error?.error?.errors);
        },
      });
  }

  clear(): void {
    this.form.reset();
    this.resetError();
    this.suggestions = [];
    this.showSuggestions = false;
    this.address = undefined;
  }

  private subscribes(): void {
    this
      .form
      .valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe(
        (value: any) => {
          const { address } = value;

          if (address) {
            this.form.controls.unit.enable();

            return;
          }

          this.form.controls.unit.disable();
        },
      );

    this.initAutocomplete();
  }

  private resetError(): void {
    this.errors = [];
  }

  private initAutocomplete(): void {
    this.searchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((input: string) => input.length >= 3),
        tap(() => {
          this.isLoadingSuggestions = true;
          this.showSuggestions = true;
          this.cdr.markForCheck();
        }),
        switchMap((input: string) => this.googleAutocompleteService.fetchSuggestions(input)),
        untilDestroyed(this),
      )
      .subscribe((suggestions: google.maps.places.AutocompleteSuggestion[]) => {
        this.suggestions = suggestions;
        this.isLoadingSuggestions = false;
        this.cdr.markForCheck();
      });
  }

  onAddressInput(event: Event): void {
    const input: string = (event.target as HTMLInputElement).value;

    this.resetError();

    if (input.length < 3) {
      this.suggestions = [];
      this.showSuggestions = false;

      return;
    }

    this.searchInput$.next(input);
  }

  onAddressKeyDown(event: KeyboardEvent): void {
    if (this.suggestionsComponent?.handleKeyDown(event)) {
      return;
    }
  }

  async onSuggestionSelected(suggestion: google.maps.places.AutocompleteSuggestion): Promise<void> {
    this.showSuggestions = false;
    this.suggestions = [];

    const place: google.maps.places.Place | null = await this.googleAutocompleteService.getPlaceDetails(suggestion);

    if (!place) {
      this.errors.push(ERROR.DEFAULT_SEARCH_ERROR);
      this.cdr.markForCheck();

      return;
    }

    const displayText: string = suggestion.placePrediction?.text?.text || '';

    this.form.controls.address.setValue(displayText, { emitEvent: false });

    this.handlePlaceChange(place);
    this.cdr.markForCheck();
  }

  onSuggestionsClosed(): void {
    this.showSuggestions = false;
    this.cdr.markForCheck();
  }

  onAddressBlur(): void {
    this.showSuggestions = false;
    this.cdr.markForCheck();
  }

  private handlePlaceChange(place: google.maps.places.Place): void {
    if (!place.addressComponents) { return; }

    const address: FormattedAddress = this
      .googlePlacesService
      .remapPlaceAddressComponents(place.addressComponents);

    const addressValid: boolean = this
      .googlePlacesService
      .validateGooglePlacesAddress(address, this.form, (error: any) => {
        this.errors = [
          ...this.errors,
          ...error,
        ];
      });

    if (addressValid) {
      const placeData: NormalizedPlaceData = this.googlePlacesService.extractPlaceData(place);

      this.address = {
        country: address.country.long_name,
        zip: address.postal_code.long_name,
        state: address.administrative_area_level_1.short_name,
        city: address?.locality?.long_name || address?.administrative_area_level_3?.long_name || address?.neighborhood?.long_name,
        street: `${address.street_number.long_name} ${address.route.long_name}`,
        lat: placeData.lat,
        lng: placeData.lng,
        place_id: placeData.placeId,
      };

      this.form.controls.unit.enable();
      this.asaplyticsService.trackInputSearch();
    }
  }

  private handleAddressChange(event: any): void {
    // Support both new format ('addressComponents') and legacy format ('address_components')
    const rawComponents: any[] = event.addressComponents || event.address_components;

    if (!rawComponents) { return; }

    // Normalize legacy format to new format if needed
    const normalizedComponents: google.maps.places.AddressComponent[] =
      this.googlePlacesService.normalizeAddressComponents(rawComponents);

    const address: FormattedAddress = this
      .googlePlacesService
      .remapPlaceAddressComponents(normalizedComponents);

    const addressValid: boolean = this
      .googlePlacesService
      .validateGooglePlacesAddress(address, this.form, (error: any) => {
        this.errors = [
          ...this.errors,
          ...error,
        ];
      });

    if (addressValid) {
      const placeData: NormalizedPlaceData = this.googlePlacesService.extractPlaceData(event);

      this.address = {
        country: address.country.long_name,
        zip: address.postal_code.long_name,
        state: address.administrative_area_level_1.short_name,
        city: address?.locality?.long_name || address?.administrative_area_level_3?.long_name || address?.neighborhood?.long_name,
        street: `${address.street_number.long_name} ${address.route.long_name}`,
        lat: placeData.lat,
        lng: placeData.lng,
        place_id: placeData.placeId,
      };

      this.form.controls.unit.enable();
      this.asaplyticsService.trackInputSearch();
    }
  }

}
